import { NextRequest, NextResponse } from 'next/server';
import type AuthenticationType from '@alltrails/types/AuthenticationType';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import OsanoConsentObject from '@alltrails/types/OsanoConsentObject';

const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;

// FB Pixel

export const getFacebookConsentValue = (osanoConsentObject: OsanoConsentObject) => {
  return osanoConsentObject.MARKETING === 'ACCEPT' ? 'grant' : 'revoke';
};

export const setFacebookConsent = (osanoConsentObject: OsanoConsentObject) => {
  if (isFacebookPixelUnavailable()) {
    return;
  }

  fbq('consent', getFacebookConsentValue(osanoConsentObject));
};

// https://developers.facebook.com/docs/meta-pixel/reference#:~:text=PAGE_VIEW-,Purchase,-When%20a%20purchase
export const trackPurchaseForFacebook = ({
  shouldReInitializePixel,
  userId,
  emailSha256,
  currencyCode,
  price,
  subscription_event_id,
  recurlyPlanCode
}: {
  shouldReInitializePixel: boolean;
  userId: number;
  emailSha256: string | null;
  currencyCode: CurrencyCode;
  price: number;
  subscription_event_id?: number;
  recurlyPlanCode: string;
}) => {
  if (isFacebookPixelUnavailable()) {
    return;
  }

  if (shouldReInitializePixel) {
    initFacebookPixel(userId, emailSha256);
  }

  fbq(
    'track',
    'Purchase',
    {
      currency: currencyCode,
      value: price,
      content_type: 'product',
      content_ids: [recurlyPlanCode]
    },
    { eventID: subscription_event_id?.toString() }
  );
};

// https://developers.facebook.com/docs/meta-pixel/reference#:~:text=ADD_TO_WISHLIST-,CompleteRegistration,-When%20a%20registration
export const trackCompleteRegistrationForFacebook = (userId: number, emailSha256: string | null, authType: AuthenticationType) => {
  if (isFacebookPixelUnavailable()) {
    return;
  }

  initFacebookPixel(userId, emailSha256);
  fbq('track', 'CompleteRegistration', { content_name: `signup - ${authType}` }, { eventID: `reg_${userId}` });
};

/**
 * Sets the _fbc and _fbp cookies based on the presence of the fbclid query parameter and existing cookies, only called when Osano consent is set to allow.
 * @see https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc/
 * @param request
 * @param response
 */
export function setFacebookCookiesMiddleware(request: NextRequest, response: NextResponse): void {
  const { searchParams } = request.nextUrl;
  const fbclid = searchParams.get('fbclid');
  const ninetyDaysFromNow = new Date(Date.now() + NINETY_DAYS_MS);

  if (fbclid) {
    const existingFbc = request.cookies.get('_fbc')?.value;
    const shouldSetFbc = !existingFbc || getFbcFbclid(existingFbc) !== fbclid;

    if (shouldSetFbc) {
      response.cookies.set({
        name: '_fbc',
        value: `fb.1.${Date.now()}.${fbclid}`,
        expires: ninetyDaysFromNow,
        domain: process.env.APEX_DOMAIN!,
        secure: true
      });
    }
  }

  if (!request.cookies.has('_fbp')) {
    const randomId = Math.floor(Math.random() * 2147483648); // 2^31

    response.cookies.set({
      name: '_fbp',
      value: `fb.1.${Date.now()}.${randomId}`,
      expires: ninetyDaysFromNow,
      domain: process.env.APEX_DOMAIN!,
      secure: true
    });
  }
}

// Private

const initFacebookPixel = (userId: number, emailSha256: string | null) => {
  fbq('init', process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID, {
    external_id: userId,
    ...(emailSha256 && { em: emailSha256 })
  });
};

// Check for presence of fbq in case of content blockers
const isFacebookPixelUnavailable = () => {
  return typeof fbq === 'undefined';
};

const getFbcFbclid = (fbc: string): string | undefined => {
  // Format: fb.1.<timestamp>.<fbclid>
  // fbclid may contain dots, so rejoin everything after the third dot
  const parts = fbc.split('.');
  if (parts.length < 4) {
    return undefined;
  }
  return parts.slice(3).join('.');
};
